import {
    CheckIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronLeftIcon, EllipsisHorizontalCircleIcon } from "@heroicons/react/24/solid";
import {
    hostedFormClientContainerKey,
    HostedFormId,
    HostedFormSlugId,
    QuestionType,
    RegionId,
    TenantRegion,
    UserId,
} from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { CopyableLink } from "components/shared/copyable-link";
import { Radio } from "components/shared/radio";
import { UserSelect } from "components/shared/user-select";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { InlineBanner } from "components/ui/inline-banner";
import { InputWithPrefix } from "components/ui/input-with-prefix";
import { defaultLinkStyle } from "components/ui/link";
import { MultiUserSelect } from "components/ui/multi-user-select";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import {
    AdvancedSearchEntityType,
    GetHostedFormByIdQuery,
    HostedFormStyle,
    useCheckHostedFormSlugQuery,
    useClaimHostedFormSlugMutation,
    useDeleteHostedFormSlugMutation,
    useGetHostedFormByIdQuery,
    usePatchHostedFormMutation,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useBridgeQuestions } from "store/selectors/bridge";
import { useFeature, useRegionId, useRegions } from "store/selectors/hooks";
import { LayoutItemExperience } from "../layout-section-item-settings";
import { LayoutSettings } from "../layout-settings";

export const AgencySettingsHostedFormDetail: React.FC<unknown> = () => {
    const { params } = useRouteMatch<{ hostedFormId: HostedFormId }>();
    const regionId = useRegionId();
    const hostedFormQuery = useGetHostedFormByIdQuery({
        regionId,
        id: params.hostedFormId,
    });

    return (
        <>
            <div className="mb-2">
                <Link to="/settings/agency/hosted-form" className={defaultLinkStyle}>
                    <div className="flex space-x-2 items-center">
                        <ChevronLeftIcon className="h-4 w-4" />
                        <span>Back to intake forms</span>
                    </div>
                </Link>
            </div>
            <QueryRenderer query={hostedFormQuery} name="AgencySettingsHostedFormDetail">
                {data => (
                    <HostedForm
                        form={data.getHostedFormById}
                        refetch={hostedFormQuery.refetch}
                    />
                )}
            </QueryRenderer>
        </>
    );
};

interface HostedFormProps {
    form: GetHostedFormByIdQuery["getHostedFormById"];
    refetch: () => void;
}

const unsupportedTypes: QuestionType[] = [
    QuestionType.number,
    QuestionType.currency,
    QuestionType.calendarDateNoYear,
    QuestionType.range,
    QuestionType.region,
    QuestionType.derived,
    QuestionType.user,
    QuestionType.textArray,
    QuestionType.customList,
];

export const HostedForm: React.FC<HostedFormProps> = props => {
    const { form, refetch } = props;
    const clientQuestions = useBridgeQuestions(AdvancedSearchEntityType.Client);
    const patchHostedFormMutation = usePatchHostedFormMutation();
    const regions = useRegions();

    const handleRegionChange = async (
        form: GetHostedFormByIdQuery["getHostedFormById"],
        regionId: RegionId,
        userId: UserId,
        recipientUserIds: UserId[],
    ) => {
        const newRegions = produce(form.configuration.regions || [], draft => {
            const existingRegion = draft.find(a => a.regionId === regionId);
            if (existingRegion) {
                existingRegion.userId = userId;
                existingRegion.recipientUserIds = recipientUserIds;
            } else {
                draft.push({ regionId, userId, recipientUserIds });
            }
        });

        await patchHostedFormMutation.mutateAsync({
            params: {
                id: form.id,
                regions: newRegions,
            },
        });
        refetch();
    };

    const updateName = useCallback(
        async (name: string) => {
            await patchHostedFormMutation.mutateAsync({
                params: {
                    id: form.id,
                    name,
                },
            });
            refetch();
        },
        [form, patchHostedFormMutation, refetch],
    );
    const updateStyle = useCallback(
        async (style: HostedFormStyle) => {
            await patchHostedFormMutation.mutateAsync({
                params: {
                    id: form.id,
                    style,
                },
            });
            refetch();
        },
        [form, patchHostedFormMutation, refetch],
    );
    const updateFileUploadEnabled = useCallback(
        async (enabled: boolean) => {
            await patchHostedFormMutation.mutateAsync({
                params: {
                    id: form.id,
                    fileUploadEnabled: enabled,
                },
            });
            refetch();
        },
        [form, patchHostedFormMutation, refetch],
    );
    const hostedFormStyle = form.configuration.style ?? HostedFormStyle.Embedded;
    const fileUploadFeatureEnabled = useFeature("hostedIntakeFormFileUpload");
    const fileUploadEnabled = form.configuration.fileUploadEnabled ?? false;

    return (
        <div className="space-y-4">
            <ContentFull>
                <ContentHeader>{form.displayName}</ContentHeader>
                <div className="space-y-2">
                    <AutosavingInput
                        initial={form.name ?? ""}
                        label="Name"
                        onCommit={updateName}
                    />

                    <Radio
                        label="Form style"
                        options={[
                            {
                                key: HostedFormStyle.Embedded,
                                text: "Embed on my website",
                                helpText:
                                    "Best used when hosted intake form is part of your website. Won't include logos or branding.",
                            },
                            {
                                key: HostedFormStyle.Standalone,
                                text: "Stand-alone page",
                                helpText:
                                    "Best used when you want to send the direct link. Will include your logo.",
                            },
                        ]}
                        value={hostedFormStyle}
                        onChange={updateStyle}
                    />

                    {fileUploadFeatureEnabled && (
                        <div className="mt-4">
                            <Toggle
                                label="Enable file uploads"
                                checked={fileUploadEnabled}
                                onChange={updateFileUploadEnabled}
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                Allow users to upload files during form submission (up to
                                10 files, 5MB each)
                            </p>
                        </div>
                    )}
                </div>
            </ContentFull>
            {regions.map(region => (
                <ContentFull key={region.id}>
                    <HostedFormRegionConfiguration
                        style={hostedFormStyle}
                        onRegionChange={(regionId, userId, recipientUserIds) =>
                            handleRegionChange(form, regionId, userId, recipientUserIds)
                        }
                        form={form}
                        region={region}
                        multiRegion={regions.length > 1}
                        refetch={refetch}
                    />
                </ContentFull>
            ))}
            <ContentFull>
                <ContentHeader>Form Layout</ContentHeader>
                {!form.customizable ? (
                    <InlineBanner type="info">
                        This layout is customizable on the <em>premium</em> plan. Contact
                        your administrator or <SupportEmailLink /> to upgrade.
                    </InlineBanner>
                ) : null}
                <LayoutSettings
                    entityMetadataName="Client"
                    includeStandardFields={true}
                    questions={clientQuestions}
                    sectionParentKey={hostedFormClientContainerKey(form.id)}
                    label=""
                    allowEditOrder={form.customizable}
                    allowEditVisibility={form.customizable}
                    mode="alwaysEditing"
                    showContainerTitle={false}
                    allowCreateSections={false}
                    allowEditIcon="always"
                    allowEditTitle="always"
                    itemExperience={LayoutItemExperience.hostedForm}
                    disabled={!form.customizable}
                    excludeQuestionTypes={unsupportedTypes}
                />
            </ContentFull>
        </div>
    );
};

interface HostedFormRegionConfigurationProps {
    region: TenantRegion;
    form: GetHostedFormByIdQuery["getHostedFormById"];
    multiRegion: boolean;
    onRegionChange: (
        regionId: RegionId,
        userId: UserId,
        recipientUserIds: UserId[],
    ) => void;
    style: HostedFormStyle;
    refetch: () => void;
}

const HostedFormRegionConfiguration: React.FC<
    HostedFormRegionConfigurationProps
> = props => {
    const { refetch, region, form, multiRegion, onRegionChange, style } = props;

    const directUrl = `${window.location.origin}/hosted-form/${form.id}?regionId=${region.id}`;
    const directUrlForSlug = (slug: string) =>
        `${window.location.origin}/hosted-form/${slug}`;
    const embedUrl = `${window.location.origin}/hosted-form/${form.id}?regionId=${region.id}&mode=embed`;
    const slugs = form.slugs?.filter(s => s.regionId === region.id) || [];
    const [slug, setSlug] = useState("");
    const [candidateSlug, setCandidateSlug] = useState("");
    const handleSlugChange = useCallback(() => setCandidateSlug(slug), [slug]);
    const deleteSlugMutation = useDeleteHostedFormSlugMutation();
    const deleteSlug = async (slugId: HostedFormSlugId) => {
        await deleteSlugMutation.mutateAsync({
            hostedFormSlugId: slugId,
        });
        refetch();
    };
    const handleSlugInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const input = e.target.value;
            // Convert to lowercase and replace any character that's not a lowercase letter, hyphen, or digit with a dash
            const sanitized = input.toLowerCase().replace(/[^a-z0-9-]/g, "-");
            setSlug(sanitized);
        },
        [],
    );
    const checkSlug = useCheckHostedFormSlugQuery(
        { slug: candidateSlug },
        { enabled: !!candidateSlug },
    );
    const claimSlugMutation = useClaimHostedFormSlugMutation();
    const [isWorking, setIsWorking] = useState(false);
    const claimSlug = async () => {
        setIsWorking(true);
        await claimSlugMutation.mutateAsync({
            claim: {
                slug: candidateSlug,
                hostedFormId: form.id,
                regionId: region.id,
            },
        });
        setSlug("");
        setCandidateSlug("");
        setIsWorking(false);
        refetch();
    };
    const currentRegion = form.configuration.regions?.find(a => a.regionId === region.id);
    const regionAssigneeUserId = currentRegion?.userId || null;
    const recipients = currentRegion?.recipientUserIds || [];

    return (
        <div className="space-y-3">
            {multiRegion ? <h3>{region.name}</h3> : null}
            <div>
                <UserSelect
                    label="Assignee for new clients"
                    value={regionAssigneeUserId}
                    onChange={userId =>
                        onRegionChange(region.id, userId as UserId, recipients)
                    }
                    includeEveryone={false}
                    includeUnassigned={true}
                />
            </div>
            <div>
                <MultiUserSelect
                    value={recipients}
                    onChange={recipientIds =>
                        onRegionChange(region.id, regionAssigneeUserId, recipientIds)
                    }
                    label="Notify these users of new clients"
                    regionId={region.id}
                />
            </div>
            {style === HostedFormStyle.Embedded ? (
                <div>
                    <p>Embed code</p>
                    <CopyableLink
                        url={`<iframe style="width: 100%; height: 1800px; border: 0;" src="${embedUrl}" id="__SP_HostedForm_${form.id}"></iframe>`}
                    />
                </div>
            ) : null}
            {style !== HostedFormStyle.Embedded ? (
                <div className="space-y-2">
                    <div className="space-y-2">
                        <span>Direct link</span>
                        <ul className="space-y-2">
                            <li className="flex space-x-2">
                                <div className="flex-1">
                                    <CopyableLink url={directUrl} />
                                </div>
                                <div className="w-4 flex-none" />
                            </li>
                            {slugs.map(s => (
                                <li key={s.id} className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <CopyableLink url={directUrlForSlug(s.slug)} />
                                    </div>
                                    <div className="w-4 flex-none">
                                        <DeleteButton
                                            onClick={() => deleteSlug(s.id)}
                                            backgroundColor="bg-white"
                                            confirm={{
                                                title: "Are you sure you want to delete this custom link?",
                                                message:
                                                    "This action cannot be undone. If this link has been shared it will no longer work.",
                                            }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="flex items-start space-x-2">
                        <div className="flex-1 items-end">
                            <InputWithPrefix
                                label="Add custom link"
                                placeholder="my-custom-link"
                                value={slug}
                                onChange={handleSlugInputChange}
                                prefix={`${window.location.origin}/hosted-form/`}
                            />
                            {checkSlug.data &&
                                !checkSlug.data.checkHostedFormSlug?.available &&
                                candidateSlug && (
                                    <div className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                                        <XMarkIcon className="w-4 h-4" />
                                        <span>
                                            {checkSlug.data.checkHostedFormSlug
                                                ?.errorMessage ||
                                                "This link is already taken"}
                                        </span>
                                    </div>
                                )}
                        </div>
                        <div className="flex-none">
                            <div className="mb-1" style={{ height: "22px" }}></div>
                            <div className="flex space-x-2">
                                <SecondaryButton onClick={handleSlugChange}>
                                    <div className="flex items-center space-x-2">
                                        {!slug ? (
                                            <EllipsisHorizontalCircleIcon className="w-4 h-4 text-gray-300" />
                                        ) : slug !== candidateSlug ? (
                                            <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
                                        ) : checkSlug.isLoading ? (
                                            <Spinner />
                                        ) : checkSlug.data?.checkHostedFormSlug
                                              ?.available ? (
                                            <CheckIcon className="w-4 h-4 text-green-500" />
                                        ) : (
                                            <XMarkIcon className="w-4 h-4 text-red-500" />
                                        )}
                                        <span>Check availability</span>
                                    </div>
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={claimSlug}
                                    disabled={
                                        !checkSlug.data?.checkHostedFormSlug?.available ||
                                        isWorking
                                    }>
                                    {isWorking ? <Spinner /> : <span>Add link</span>}
                                </PrimaryButton>
                                {/* horiz offset for the delete button */}
                                <div className="w-4 flex-none" />
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
